import { IconBell } from "@tabler/icons-react";
import React, { useEffect, useRef, useState } from "react";
import {
  Menu,
  Container,
  Badge,
  createStyles,
  keyframes,
  rem,
  ActionIcon,
  Text,
} from "@mantine/core";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "@/stores";
import { resetWsUnreadCount } from "@/slides/notificationBell/notificationBellSlice";
import NotificationsDrawer from "./drawers/NotificationsDrawer";
import NotificationDetailDrawer from "./drawers/NotificationDetailDrawer";
import FilterDrawer from "./drawers/FilterDrawer";
import {
  DrawerType,
  FILTER,
  NOTIFICATION_DETAIL,
  NOTIFICATIONS,
} from "./constants/drawer.constant";
import { NotificationFilterState } from "./types/type";
import { useGetNotificationStickyQuery } from "services/notificationAPI";
import { NsType } from "@/dto/notifications/response/enum";
import useNotificationWebSocket from "@/hooks/useNotificationWebSocket";

const NOTIFICATION_MAXIMUM = "99+";

const bellShake = keyframes({
  "0%":   { transform: "rotate(0deg)" },
  "15%":  { transform: "rotate(18deg)" },
  "30%":  { transform: "rotate(-16deg)" },
  "45%":  { transform: "rotate(12deg)" },
  "60%":  { transform: "rotate(-10deg)" },
  "75%":  { transform: "rotate(6deg)" },
  "90%":  { transform: "rotate(-4deg)" },
  "100%": { transform: "rotate(0deg)" },
});

const useStyles = createStyles(() => ({
  badgeWrapper: {
    position: "relative",
    display: "inline-block",
  },
  bellShaking: {
    transformOrigin: "top center",
    animation: `${bellShake} 0.6s ease-in-out`,
  },
}));

const INITIAL_FILTER_STATE: NotificationFilterState = {
  dateRange: { from: null, to: null },
  status: "personal",
  types: "ALL",
  keyword: "",
};

export const NotificationsMenu: React.FC = () => {
  const { classes } = useStyles();
  const dispatch = useDispatch<AppDispatch>();
  const [activeDrawer, setActiveDrawer] = useState<DrawerType>(null);
  const [filterState, setFilterState] =
    useState<NotificationFilterState>(INITIAL_FILTER_STATE);

  // Connect WebSocket for real-time badge increments
  useNotificationWebSocket();

  // WS-driven incremental count (reset when user opens the drawer)
  const wsUnreadCount = useSelector(
    (state: RootState) => state.notificationBell.wsUnreadCount,
  );

  // Bell shake animation — fires for 600 ms each time a new WS notification arrives
  const [isBellShaking, setIsBellShaking] = useState(false);
  const prevWsCountRef = useRef(0);
  useEffect(() => {
    if (wsUnreadCount > prevWsCountRef.current) {
      prevWsCountRef.current = wsUnreadCount;
      setIsBellShaking(true);
      const t = setTimeout(() => setIsBellShaking(false), 650);
      return () => clearTimeout(t);
    }
    prevWsCountRef.current = wsUnreadCount;
    return undefined;
  }, [wsUnreadCount]);

  // Unread count comes from getNotificationSticky's notifyUnview field (no separate API)
  const { data: stickyData } = useGetNotificationStickyQuery({
    nsType: NsType.Private,
    pageNo: 1,
    pageSize: 20,
  });
  const apiUnreadCount = stickyData?.body?.notifyUnview ?? 0;

  // Badge = API unread + WS messages not yet reflected in API
  const totalUnread = apiUnreadCount + wsUnreadCount;

  const handleBellClick = () => {
    dispatch(resetWsUnreadCount());
    setActiveDrawer("notifications");
  };

  const drawerComponents: Record<
    Exclude<DrawerType, null>,
    React.ComponentType<any>
  > = {
    [NOTIFICATIONS]: NotificationsDrawer,
    [NOTIFICATION_DETAIL]: NotificationDetailDrawer,
    [FILTER]: FilterDrawer,
  };

  const ActiveDrawer = activeDrawer && drawerComponents[activeDrawer];

  return (
    <Menu shadow="md" width={500}>
      <Menu.Target>
        <Container onClick={handleBellClick} sx={{ padding: 0 }}>
          <div className={classes.badgeWrapper}>
            <ActionIcon
              variant="light"
              className={isBellShaking ? classes.bellShaking : undefined}
              sx={{
                backgroundColor: "transparent",
                "&:hover": { backgroundColor: "transparent" },
                color: "#2E3337",
              }}
            >
              <IconBell size={24} stroke={1.5} />
            </ActionIcon>

            {totalUnread > 0 && (
              <Badge
                radius="50%"
                bg="#E6492D"
                size="md"
                sx={{
                  position: "absolute",
                  top: rem(4),
                  right: rem(1),
                  transform: "translate(50%, -50%)",
                  pointerEvents: "none",
                  padding: 4,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Text size={10} color="#fff">
                  {totalUnread > 99 ? NOTIFICATION_MAXIMUM : totalUnread}
                </Text>
              </Badge>
            )}
          </div>
        </Container>
      </Menu.Target>

      {ActiveDrawer && (
        <ActiveDrawer
          activeDrawer={activeDrawer}
          setActiveDrawer={setActiveDrawer}
          filterState={filterState}
          setFilterState={setFilterState}
        />
      )}
    </Menu>
  );
};

export default NotificationsMenu;
